/**
 * 旅一覧ページ
 *
 * タブで「旅の記録」と「制覇マップ」を切り替える。
 */

import { MapPinned } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Header } from '@/components/shared/header'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PATHS } from '@/routes/paths'
import { useTripStore } from '@/store/useTripStore'

import { ConquestMapView } from './components/conquest-map'
import { TripCard } from './components/trip-card'

export const TripsPage = () => {
  const navigate = useNavigate()
  const [tab, setTab] = useState('trips')

  const trips = useTripStore((s) => s.trips)
  const isTripsLoading = useTripStore((s) => s.isTripsLoading)
  const conquest = useTripStore((s) => s.conquest)
  const isConquestLoading = useTripStore((s) => s.isConquestLoading)
  const error = useTripStore((s) => s.error)
  const fetchTrips = useTripStore((s) => s.fetchTrips)
  const fetchConquest = useTripStore((s) => s.fetchConquest)
  const clearError = useTripStore((s) => s.clearError)

  useEffect(() => {
    void fetchTrips()
  }, [fetchTrips])

  // 制覇マップは開いたときに初回だけ取得する
  useEffect(() => {
    if (tab !== 'conquest' || conquest) return
    void fetchConquest()
  }, [tab, conquest, fetchConquest])

  useEffect(() => {
    if (!error) return
    toast.error(error)
    clearError()
  }, [error, clearError])

  const visibleTrips = useMemo(() => trips.filter((t) => t.status !== 'dismissed'), [trips])

  return (
    <div className="flex h-dvh flex-col">
      <Header title="旅の記録" onClose={() => navigate(PATHS.HOME)} />

      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <TabsList className="mx-3 mt-3 grid w-auto grid-cols-2">
          <TabsTrigger value="trips">旅の記録</TabsTrigger>
          <TabsTrigger value="conquest">制覇マップ</TabsTrigger>
        </TabsList>

        <TabsContent value="trips" className="min-h-0 flex-1 overflow-y-auto p-3">
          {isTripsLoading && visibleTrips.length === 0 ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-56 w-full rounded-xl" />
              ))}
            </div>
          ) : visibleTrips.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
              <MapPinned className="size-10" />
              <p className="text-sm">まだ旅の記録がありません</p>
              <p className="text-xs">遠出から帰宅すると、翌日までに自動で追加されます</p>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleTrips.map((trip) => (
                <TripCard
                  key={trip.id}
                  trip={trip}
                  onClick={() => navigate(PATHS.TRIPS.DETAIL.replace(':trip_id', String(trip.id)))}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="conquest" className="min-h-0 flex-1 overflow-y-auto p-3">
          {isConquestLoading && !conquest ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-32 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <ConquestMapView prefectures={conquest?.prefectures ?? []} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
